import { useGT } from 'gt-react';
import { TrashIcon } from '@phosphor-icons/react';
import { UPLOAD_COLUMNS, type UploadColumn } from '@erp/rpc';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  MappingDraft,
  MappingDraftError,
  MappingKind,
  SourceFormat,
} from './layoutForm';
import { useRuleText } from './useRuleText';

const KINDS: MappingKind[] = [
  'source',
  'constant',
  'rowNumber',
  'flag',
  'formula',
];
const FORMATS: SourceFormat[] = ['text', 'date', 'number', 'substring'];

function pick<T extends string>(options: readonly T[]) {
  return (value: unknown): value is T =>
    options.some((option) => option === value);
}
const isColumn = pick(UPLOAD_COLUMNS);
const isKind = pick(KINDS);
const isFormat = pick(FORMATS);

interface Props {
  draft: MappingDraft;
  error: MappingDraftError | undefined;
  /** The dropped workbook's headers, offered as suggestions. */
  headerListId: string;
  onChange: (changes: Partial<MappingDraft>) => void;
  onRemove: () => void;
}

/** One upload column's mapping in the layout editor. */
export function MappingRow({
  draft,
  error,
  headerListId,
  onChange,
  onRemove,
}: Props) {
  const gt = useGT();
  const { columnLabel } = useRuleText();
  const ctx = {
    context: 'upload layout editor: how an upload column is filled',
  };
  const kindNames: Record<MappingKind, string> = {
    source: gt('From a column', ctx),
    constant: gt('Always the value', ctx),
    rowNumber: gt('Row number (1, 2, 3…)', ctx),
    flag: gt('Value when filled', ctx),
    formula: gt('Formula', ctx),
  };

  return (
    <div className="flex flex-col gap-1 border-b py-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <ColumnSelect
          value={draft.column}
          onChange={(column) => onChange({ column })}
          label={columnLabel}
        />
        <Select
          value={draft.kind}
          onValueChange={(value) => {
            if (isKind(value)) onChange({ kind: value });
          }}
        >
          <SelectTrigger className="h-8 w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {KINDS.map((kind) => (
              <SelectItem key={kind} value={kind}>
                {kindNames[kind]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <KindInputs
          draft={draft}
          headerListId={headerListId}
          onChange={onChange}
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={onRemove}
          aria-label={gt('Remove mapping', {
            context:
              'button that removes one column mapping from an upload layout',
          })}
        >
          <TrashIcon className="h-4 w-4" />
        </Button>
      </div>
      {error ? <MappingErrorText error={error} /> : null}
    </div>
  );
}

function ColumnSelect({
  value,
  onChange,
  label,
}: {
  value: UploadColumn;
  onChange: (column: UploadColumn) => void;
  label: (column: UploadColumn) => string;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (isColumn(next)) onChange(next);
      }}
    >
      <SelectTrigger className="h-8 w-52">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {UPLOAD_COLUMNS.map((column) => (
          <SelectItem key={column} value={column}>
            {label(column)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function HeaderInput({
  value,
  listId,
  onChange,
}: {
  value: string;
  listId: string;
  onChange: (header: string) => void;
}) {
  const gt = useGT();
  return (
    <Input
      className="h-8 w-56"
      list={listId}
      placeholder={gt('Input column header', {
        context: 'placeholder: which column of the supplier workbook to read',
      })}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function KindInputs({
  draft,
  headerListId,
  onChange,
}: {
  draft: MappingDraft;
  headerListId: string;
  onChange: (changes: Partial<MappingDraft>) => void;
}) {
  const gt = useGT();
  const ctx = { context: 'upload layout editor input' };
  switch (draft.kind) {
    case 'rowNumber':
      return null;
    case 'constant':
      return (
        <Input
          className="h-8 w-40"
          placeholder={gt('Value, e.g. Y', ctx)}
          value={draft.value}
          onChange={(event) => onChange({ value: event.target.value })}
        />
      );
    case 'source':
      return (
        <SourceInputs
          draft={draft}
          headerListId={headerListId}
          onChange={onChange}
        />
      );
    case 'flag':
      return (
        <FlagInputs
          draft={draft}
          headerListId={headerListId}
          onChange={onChange}
        />
      );
    case 'formula':
      return (
        <>
          <Input
            className="h-8 min-w-80 flex-1 font-mono text-xs"
            placeholder={gt('e.g. [SAP COST] * (1 + DEFAULT([ADDER], 0))', ctx)}
            value={draft.expression}
            onChange={(event) => onChange({ expression: event.target.value })}
          />
          <DecimalsInput draft={draft} optional onChange={onChange} />
          <label className="flex items-center gap-1 text-xs">
            <Checkbox
              checked={draft.blankWhenZero}
              onCheckedChange={(checked) =>
                onChange({ blankWhenZero: checked === true })
              }
            />
            {gt('Blank when 0', ctx)}
          </label>
        </>
      );
  }
}

function DecimalsInput({
  draft,
  optional = false,
  onChange,
}: {
  draft: MappingDraft;
  /** A formula may leave decimals blank: written as computed. */
  optional?: boolean;
  onChange: (changes: Partial<MappingDraft>) => void;
}) {
  const gt = useGT();
  const ctx = { context: 'upload layout editor input' };
  const label = optional
    ? gt('Decimals (blank = as is)', ctx)
    : gt('Decimals', ctx);
  return (
    <Input
      className="h-8 w-24"
      inputMode="numeric"
      placeholder={label}
      title={label}
      value={draft.decimals}
      onChange={(event) => onChange({ decimals: event.target.value })}
    />
  );
}

function SourceInputs({
  draft,
  headerListId,
  onChange,
}: {
  draft: MappingDraft;
  headerListId: string;
  onChange: (changes: Partial<MappingDraft>) => void;
}) {
  const gt = useGT();
  const ctx = {
    context: 'upload layout editor: how a source value is written',
  };
  const formatNames: Record<SourceFormat, string> = {
    text: gt('As text', ctx),
    date: gt('As date YYMMDD', ctx),
    number: gt('As number', ctx),
    substring: gt('Part of the text', ctx),
  };
  return (
    <>
      <HeaderInput
        value={draft.header}
        listId={headerListId}
        onChange={(header) => onChange({ header })}
      />
      <Select
        value={draft.format}
        onValueChange={(value) => {
          if (isFormat(value)) onChange({ format: value });
        }}
      >
        <SelectTrigger className="h-8 w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FORMATS.map((format) => (
            <SelectItem key={format} value={format}>
              {formatNames[format]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {draft.format === 'number' ? (
        <DecimalsInput draft={draft} onChange={onChange} />
      ) : null}
      {draft.format === 'substring' ? (
        <>
          <Input
            className="h-8 w-24"
            inputMode="numeric"
            placeholder={gt('From (0 = first)', ctx)}
            title={gt('From (0 = first)', ctx)}
            value={draft.start}
            onChange={(event) => onChange({ start: event.target.value })}
          />
          <Input
            className="h-8 w-24"
            inputMode="numeric"
            placeholder={gt('Length (all)', ctx)}
            title={gt('Length (all)', ctx)}
            value={draft.length}
            onChange={(event) => onChange({ length: event.target.value })}
          />
        </>
      ) : null}
      <OnlyWhenFilledSelect
        value={draft.onlyWhenFilled}
        onChange={(onlyWhenFilled) => onChange({ onlyWhenFilled })}
      />
    </>
  );
}

/** Optionally write a source value only when another upload column is filled. */
function OnlyWhenFilledSelect({
  value,
  onChange,
}: {
  value: UploadColumn | '';
  onChange: (column: UploadColumn | '') => void;
}) {
  const gt = useGT();
  const { columnLabel } = useRuleText();
  const always = '__always__';
  return (
    <Select
      value={value === '' ? always : value}
      onValueChange={(next) => {
        if (next === always) onChange('');
        else if (isColumn(next)) onChange(next);
      }}
    >
      <SelectTrigger
        className="h-8 w-52"
        title={gt('Write only when this upload column is filled', {
          context: 'upload layout editor: optional condition on a source value',
        })}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={always}>
          {gt('Always', {
            context: 'upload layout editor: a source value is always written',
          })}
        </SelectItem>
        {UPLOAD_COLUMNS.map((column) => (
          <SelectItem key={column} value={column}>
            {gt('Only if {column} filled', {
              column: columnLabel(column),
              context:
                'upload layout editor: write only when that upload column is filled',
            })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FlagInputs({
  draft,
  headerListId,
  onChange,
}: {
  draft: MappingDraft;
  headerListId: string;
  onChange: (changes: Partial<MappingDraft>) => void;
}) {
  const gt = useGT();
  const { columnLabel } = useRuleText();
  const ctx = {
    context: 'upload layout editor: a value written when something is filled',
  };
  return (
    <>
      <Input
        className="h-8 w-16"
        value={draft.value}
        title={gt('Value to write', ctx)}
        onChange={(event) => onChange({ value: event.target.value })}
      />
      <span className="text-muted-foreground text-xs">{gt('when', ctx)}</span>
      <Select
        value={draft.flagOn}
        onValueChange={(value) => {
          if (value === 'header' || value === 'column')
            onChange({ flagOn: value });
        }}
      >
        <SelectTrigger className="h-8 w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="header">{gt('input column', ctx)}</SelectItem>
          <SelectItem value="column">{gt('upload column', ctx)}</SelectItem>
        </SelectContent>
      </Select>
      {draft.flagOn === 'header' ? (
        <HeaderInput
          value={draft.header}
          listId={headerListId}
          onChange={(header) => onChange({ header })}
        />
      ) : (
        <ColumnSelect
          value={draft.flagColumn}
          onChange={(flagColumn) => onChange({ flagColumn })}
          label={columnLabel}
        />
      )}
      <span className="text-muted-foreground text-xs">
        {gt('is filled', ctx)}
      </span>
    </>
  );
}

function MappingErrorText({ error }: { error: MappingDraftError }) {
  const gt = useGT();
  const ctx = { context: 'upload layout editor validation error' };
  const messages = {
    header: gt('Enter the input column header.', ctx),
    value: gt('Enter the value to write.', ctx),
    number: gt('Enter whole numbers (decimals 0–6).', ctx),
    lookup: gt('Fill the second file’s settings, or leave them blank.', ctx),
  };
  return (
    <p className="text-destructive text-xs">
      {'message' in error ? error.message : messages[error.field]}
    </p>
  );
}
